import React, { ReactNode, useLayoutEffect, useMemo } from 'react';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { Helmet } from 'react-helmet-async';

import { generateBorderSizeValues } from 'src/theme/borders';
import { resolveBrandForeground } from 'src/theme/brandForeground';
import {
  alert,
  black,
  dark,
  light,
  link,
  success,
  warning,
  white,
  getColorShades,
} from 'src/theme/colors';
import {
  syncTenantTheme,
  TENANT_BG_THEMING_ENABLED,
} from 'src/theme/tenantTheme';
import { useCompany } from './Company.store';
import { cacheBrandColor, clearCachedBrandColor } from './brandColorCache';

// The new (shadcn / Untitled UI) portal renders in Geist by default. Keep it as
// the fallback after any tenant-selected font so weights the brand font lacks
// still resolve sensibly.
const GEIST_FALLBACK = `'Geist Sans', 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

// Runtime-overridable font tokens: --font-sans drives the shadcn `.wh-ui` scope
// + Radix portals (tailwind.css); --font-body/--font-display drive the Untitled
// UI layer (theme.css). Overriding all three applies a brand font across the
// whole new portal.
const PORTAL_FONT_VARS = ['--font-sans', '--font-body', '--font-display'];

// Named families already loaded by index.html / the Geist CDN — selecting one
// overrides Geist but needs no extra Google Fonts request.
const BUNDLED_FONTS = new Set([
  'Poppins',
  'IBM Plex Sans',
  'Geist',
  'Geist Sans',
]);

export const ThemeStoreProvider = ({ children }: { children: ReactNode }) => {
  const { data: company } = useCompany();

  // Drive the brand accent from the company's "Main color (Dark)"
  // (primary_color): --brand-navy is the accent token behind --color-primary,
  // the sidebar's active nav item, and the loading spinner, so overriding it
  // makes every accent follow the tenant primary instead of the static navy.
  // --spinner-color is set explicitly too (its tokens.css default is
  // var(--brand-navy), but the boot splash reads --spinner-color directly).
  // --brand-foreground is derived from the resolved accent's luminance so
  // everything drawn ON it (`text-primary-foreground`) stays legible for a
  // light tenant brand as well as a dark one — see src/theme/brandForeground.
  // All fall back to the static navy defaults when primary_color is unset.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (company.primary_color) {
      root.style.setProperty('--brand-navy', company.primary_color);
      root.style.setProperty('--spinner-color', company.primary_color);
      root.style.setProperty(
        '--brand-foreground',
        resolveBrandForeground(company.primary_color)
      );
      // Persist for the next reload so the spinner is brand-coloured from the
      // first frame (read synchronously in src/index.tsx).
      cacheBrandColor(company.primary_color);
    } else {
      root.style.removeProperty('--brand-navy');
      root.style.removeProperty('--spinner-color');
      root.style.removeProperty('--brand-foreground');
      // Drop the persisted color too, so a tenant that unsets primary_color
      // doesn't keep repainting the stale brand color on every reload.
      clearCachedBrandColor();
    }
  }, [company.primary_color]);

  // Drive the portal's corner radii from the tenant's backend border_radius.
  // The shadcn `--radius` lives in @theme inline (tailwind.css), whose values
  // are baked into the generated `rounded-*` utilities at build time and can't
  // be overridden at runtime — so we expose the full radius scale as real,
  // runtime-settable CSS vars here (defaulted in tokens.css), mirroring the
  // theme.borders.{xs,s,m,l,xl} scale used on the styled-components side.
  // Consumers: nav + banner use `--wh-radius` (base = s step); cards/panels use
  // `--wh-radius-l`. The 'medium' default preserves the original 8px/16px look.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const borders = generateBorderSizeValues(company.border_radius || 'medium');
    root.style.setProperty('--wh-radius-xs', borders.xs);
    root.style.setProperty('--wh-radius-s', borders.s);
    root.style.setProperty('--wh-radius-m', borders.m);
    root.style.setProperty('--wh-radius-l', borders.l);
    root.style.setProperty('--wh-radius-xl', borders.xl);
    // Base alias (= s) kept for existing `rounded-[var(--wh-radius,8px)]` uses.
    root.style.setProperty('--wh-radius', borders.s);
    // shadcn base radius → drives every rounded-sm/md/lg/xl app-wide (see
    // tailwind.css: --radius lives in :root, derived steps scale off it).
    root.style.setProperty('--radius', borders.s);
  }, [company.border_radius]);

  // Paint the app-wide background from the tenant's "Secondary color (Light)"
  // and, when it is dark, switch the whole app into dark mode (see
  // src/theme/tenantTheme.ts). Theme.store is the SOLE owner of the `dark-mode`
  // class — the ThemeProvider's toggling is retired (Task 4). Gated by
  // TENANT_BG_THEMING_ENABLED: while disabled we pass undefined so the app
  // ignores secondary_color and resets to the default light/white look.
  useLayoutEffect(() => {
    syncTenantTheme(
      document.documentElement,
      TENANT_BG_THEMING_ENABLED ? company.secondary_color : undefined
    );
  }, [company.secondary_color]);

  // Resolve the tenant's "Main font" for the NEW portal surfaces. An uploaded
  // font wins (its @font-face is injected below); otherwise the named dropdown
  // selection. Poppins (the backend default) and an unset value both map to the
  // portal's built-in Geist, so we leave the tokens untouched in that case.
  const portalFontFamily = company.main_portal_font
    ? 'CustomPrimaryFont'
    : company.main_portal_font_family &&
        company.main_portal_font_family !== 'Poppins'
      ? company.main_portal_font_family
      : null;

  // A named (non-uploaded) family that isn't already bundled must be fetched
  // from Google Fonts to actually render; otherwise the stack falls back to
  // Geist. Best-effort: a name not hosted on Google Fonts simply 404s.
  //
  // Derived from the already-resolved portalFontFamily so "which font is active"
  // has a single source of truth (it already excludes uploaded fonts as
  // 'CustomPrimaryFont' and maps Poppins/unset to null). encodeURIComponent
  // keeps metacharacters in the tenant-supplied name (&, #, =, …) from
  // corrupting the query string; spaces use '+' per Google's css2 format.
  const googleFontHref =
    portalFontFamily &&
    portalFontFamily !== 'CustomPrimaryFont' &&
    !BUNDLED_FONTS.has(portalFontFamily)
      ? `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
          portalFontFamily
        ).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`
      : null;

  // Drive the new portal's base font from the resolved brand font, mirroring the
  // --spinner-color pattern above. Legacy antd pages set their body font
  // directly in index.css and are unaffected.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (portalFontFamily) {
      const stack = `'${portalFontFamily}', ${GEIST_FALLBACK}`;
      PORTAL_FONT_VARS.forEach((v) => root.style.setProperty(v, stack));
    } else {
      PORTAL_FONT_VARS.forEach((v) => root.style.removeProperty(v));
    }
  }, [portalFontFamily]);

  const theme = useMemo<DefaultTheme>(
    () => ({
      colors: {
        ...getColorShades('dark', company.primary_color || dark),
        ...getColorShades('light', company.secondary_color || light),
        ...getColorShades('white', white),
        ...getColorShades('black', black),
        ...getColorShades('alert', alert),
        ...getColorShades('success', success),
        ...getColorShades('warning', warning),
        ...getColorShades('link', link),
      },
      borders: generateBorderSizeValues(company.border_radius || 'medium'),
      fonts: {
        primary: company.main_portal_font
          ? 'CustomPrimaryFont'
          : company.main_portal_font_family || 'Poppins',
        secondary: company.secondary_portal_font
          ? 'CustomSecondaryFont'
          : company.secondary_portal_font_family || 'Poppins',
      },
    }),
    [company]
  );

  return (
    <>
      <Helmet
        link={
          googleFontHref
            ? [{ rel: 'stylesheet', href: googleFontHref }]
            : undefined
        }
        style={[
          {
            cssText: `${
              company.main_portal_font
                ? `
                  @font-face {
                    font-family: 'CustomPrimaryFont';
                    src: url("${company.main_portal_font}");
                  }
                `
                : ''
            }\n${
              company.secondary_portal_font
                ? `
                  @font-face {
                    font-family: 'CustomSecondaryFont';
                    src: url("${company.secondary_portal_font}");
                  }
                `
                : ''
            }`,
          },
        ]}
      />
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </>
  );
};
